import React, { useState, useEffect, Fragment, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import { AnimatePresence, motion } from 'framer-motion';
import VacanciesTab from '../components/messenger/VacanciesTab';
import { useBrandingColors } from '../hooks/useBrandingColors';
import { useVacancyStore } from '../store/vacancyStore';
import { useAuthStore } from '../store/authStore';
import { getCampaigns, getCampaignById, startCampaign, stopCampaign, archiveCampaign, getCampaignStats } from '../services/aiRecruiterService';
import type { Campaign } from '../types/aiRecruiter';
import toast from 'react-hot-toast';
import hhIcon from '../assets/icons/channels/hh.svg';
import telegramIcon from '../assets/icons/channels/telegram.svg';
import whatsappIcon from '../assets/icons/channels/whatsapp.svg';
import maxIcon from '../assets/icons/channels/max.svg';
import avitoIcon from '../assets/icons/channels/avito.svg';
import { MAILINGS_NAV_STATE_KEY } from '../constants/clarifyExperience';

interface MailingsPageProps {
  /** When true, the page is rendered inside another form; trims internal horizontal padding so content aligns with the host form's gutter. */
  embedded?: boolean;
  externalSaveRef?: React.MutableRefObject<(() => Promise<boolean>) | null>;
  externalStartRef?: React.MutableRefObject<(() => Promise<void>) | null>;
  externalStopRef?: React.MutableRefObject<(() => Promise<void>) | null>;
  onStartAvailableChange?: (available: boolean) => void;
  onStopAvailableChange?: (available: boolean) => void;
}

type CampaignChannelCode = 'HH' | 'TG' | 'WA' | 'MAX' | 'AVITO';
type CampaignChannelSettings = {
  accountSettings?: Campaign['accountSettings'];
  hhVacancyId?: Campaign['hhVacancyId'];
};

const CAMPAIGN_CHANNEL_CODES: CampaignChannelCode[] = ['HH', 'TG', 'WA', 'MAX', 'AVITO'];

const normalizeCampaignChannel = (value: unknown): CampaignChannelCode | null => {
  switch (String(value ?? '').toUpperCase()) {
    case 'HH':
      return 'HH';
    case 'TELEGRAM':
    case 'TG':
      return 'TG';
    case 'WHATSAPP':
    case 'WA':
      return 'WA';
    case 'MAX':
      return 'MAX';
    case 'AVITO':
      return 'AVITO';
    default:
      return null;
  }
};

const hasFilledValue = (value: unknown): boolean => value != null && String(value).trim() !== '';

export const getEnabledCampaignChannels = (campaign?: CampaignChannelSettings | null): CampaignChannelCode[] => {
  const enabledMessengers = campaign?.accountSettings?.enabledMessengers;
  if (!Array.isArray(enabledMessengers)) {
    return [];
  }

  return Array.from(new Set(enabledMessengers
    .map(normalizeCampaignChannel)
    .filter((channel): channel is CampaignChannelCode => channel !== null)));
};

export const isCampaignChannelReady = (
  campaign: CampaignChannelSettings | null | undefined,
  channel: CampaignChannelCode,
): boolean => {
  if (!getEnabledCampaignChannels(campaign).includes(channel)) {
    return false;
  }

  const settings = campaign?.accountSettings;
  if (channel === 'HH') return hasFilledValue(campaign?.hhVacancyId);
  if (channel === 'TG') return hasFilledValue(settings?.telegramAccountId);
  if (channel === 'WA') return hasFilledValue(settings?.whatsappAccountId);
  if (channel === 'MAX') return hasFilledValue(settings?.maxAccountId);
  if (channel === 'AVITO') return true;
  return false;
};

export const hasConfiguredStartChannel = (campaign?: CampaignChannelSettings | null): boolean => {
  const enabledChannels = getEnabledCampaignChannels(campaign);
  return enabledChannels.length > 0 && enabledChannels.every(channel => isCampaignChannelReady(campaign, channel));
};

export const buildStartCampaignRequestConfig = (companyId?: number | string | null) => (
  companyId ? { companyId } : {}
);

const MailingsPage: React.FC<MailingsPageProps> = ({
  embedded = false,
  externalSaveRef,
  externalStartRef,
  externalStopRef,
  onStartAvailableChange,
  onStopAvailableChange,
}) => {
  const hPadding = embedded ? '' : 'px-8';
  const getCandidateWord = (count: number) => {
    const mod100 = count % 100;
    if (mod100 >= 11 && mod100 <= 14) {
      return 'кандидатов';
    }
    const mod10 = count % 10;
    if (mod10 === 1) {
      return 'кандидат';
    }
    if (mod10 >= 2 && mod10 <= 4) {
      return 'кандидата';
    }
    return 'кандидатов';
  };

  const hexToRgba = (hex: string, alpha: number): string => {
    const m = hex.replace('#', '');
    const r = parseInt(m.length === 3 ? m[0] + m[0] : m.slice(0, 2), 16);
    const g = parseInt(m.length === 3 ? m[1] + m[1] : m.slice(2, 4), 16);
    const b = parseInt(m.length === 3 ? m[2] + m[2] : m.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'mailings' | 'settings'>(() => embedded ? 'settings' : 'mailings');
  const [singleCampaignMode, setSingleCampaignMode] = useState(embedded);
  // Hover-состояния для переключателя видов — чтобы React не перетирал inline-style на ре-рендере.
  const [gridHover, setGridHover] = useState(false);
  const [tableHover, setTableHover] = useState(false);
  // Поиск по названию рассылки (фильтрует видимый список)
  const [searchQuery, setSearchQuery] = useState('');
  // Фильтры
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [channelFilter, setChannelFilter] = useState<string>('');
  const [statusHover, setStatusHover] = useState(false);
  const [channelHover, setChannelHover] = useState(false);
  const [searchHover, setSearchHover] = useState(false);
  const [searchFocus, setSearchFocus] = useState(false);
  const [hoveredCampaignId, setHoveredCampaignId] = useState<string | null>(null);
  const [statusOpen, setStatusOpen] = useState(false);
  const [channelOpen, setChannelOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const channelDropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target as Node)) setStatusOpen(false);
      if (channelDropdownRef.current && !channelDropdownRef.current.contains(e.target as Node)) setChannelOpen(false);
    };
    if (statusOpen || channelOpen) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [statusOpen, channelOpen]);
  const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
    { value: '', label: 'Все статусы' },
    { value: 'ACTIVE', label: 'Активна' },
    { value: 'PAUSED', label: 'Приостановлена' },
    { value: 'DRAFT', label: 'Черновик' },
    { value: 'STOPPED', label: 'Остановлена' },
    { value: 'CANCELLED', label: 'Отменена' },
    { value: 'ARCHIVED', label: 'Архив' },
  ];
  const CHANNEL_OPTIONS: Array<{ value: string; label: string }> = [
    { value: '', label: 'Все каналы' },
    { value: 'HH', label: 'HH' },
    { value: 'TG', label: 'Telegram' },
    { value: 'WA', label: 'WhatsApp' },
    { value: 'MAX', label: 'Max' },
    { value: 'AVITO', label: 'Авито' },
  ];
  // Сортировка для таблицы — кликом по заголовкам колонок
  type SortField = 'name' | 'status' | 'channels' | 'candidates';
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  // Порядок статусов кампаний для сортировки (сверху — активные, внизу — архив)
  const statusRank: Record<string, number> = { ACTIVE: 0, PAUSED: 1, DRAFT: 2, STOPPED: 3, CANCELLED: 4, ARCHIVED: 5 };
  const [campaignsView, setCampaignsView] = useState<'grid' | 'table'>(() => {
    try { return (localStorage.getItem('aiRecruiter_campaignsView') as 'grid' | 'table') || 'grid'; }
    catch { return 'grid'; }
  });
  useEffect(() => { try { localStorage.setItem('aiRecruiter_campaignsView', campaignsView); } catch {} }, [campaignsView]);
  const colors = useBrandingColors();
  const { selectedVacancyId } = useVacancyStore();

  useEffect(() => {
    if (embedded || !selectedVacancyId) return;
    navigate(`/recruiter/vacancy/edit-data/${selectedVacancyId}?tab=mailings`, { replace: true });
  }, [embedded, navigate, selectedVacancyId]);

  // Состояние для выбранной кампании (без localStorage - определяется выбором пользователя)
  const [selectedCampaign, setSelectedCampaign] = useState<string | null>(null);

  // Состояние для списка кампаний
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);

  // Количество кандидатов по каждой кампании
  const [campaignCandidateCounts, setCampaignCandidateCounts] = useState<Record<string, number>>({});
  // FR-E2E-19: Количество по источникам per-campaign
  const [, setCampaignSourceTotals] = useState<Record<string, Record<string, number>>>({});

  // Состояние для данных выбранной кампании
  const [selectedCampaignData, setSelectedCampaignData] = useState<Campaign | null>(null);

  // Ref для функции сохранения из VacanciesTab
  const internalSaveHandlerRef = useRef<(() => Promise<boolean>) | null>(null);
  const validateStartHandlerRef = useRef<(() => boolean) | null>(null);
  const saveHandlerRef = externalSaveRef ?? internalSaveHandlerRef;
  const canStartSelectedCampaign = Boolean(
    selectedCampaign &&
    selectedCampaignData &&
    String(selectedCampaignData.id) === selectedCampaign &&
    selectedCampaignData.status !== 'ARCHIVED' &&
    (
      selectedCampaignData.status === 'DRAFT' ||
      selectedCampaignData.status === 'STOPPED' ||
      selectedCampaignData.status === 'PAUSED' ||
      selectedCampaignData.status === 'CANCELLED'
    )
  );
  const canStopSelectedCampaign = Boolean(
    selectedCampaign &&
    selectedCampaignData &&
    String(selectedCampaignData.id) === selectedCampaign &&
    selectedCampaignData.status === 'ACTIVE'
  );

  // Сохраняем выбранную кампанию в localStorage (для восстановления при возврате на страницу)
  useEffect(() => {
    if (embedded) return;
    if (selectedCampaign) {
      localStorage.setItem('aiRecruiter_selectedCampaign', selectedCampaign);
    } else {
      localStorage.removeItem('aiRecruiter_selectedCampaign');
    }
  }, [embedded, selectedCampaign]);

  // TASKNEIROKLYUCH-465: открываем настройки конкретной рассылки, если на страницу
  // пришли из откликов через navigate(..., { state: { openCampaign: { campaignId } } }).
  // Чистим state после применения, чтобы при F5/возврате не происходило повторное открытие.
  useEffect(() => {
    const navState = (location.state || null) as Record<string, any> | null;
    const openPayload = navState?.[MAILINGS_NAV_STATE_KEY];
    if (!openPayload?.campaignId) return;
    setSelectedCampaign(String(openPayload.campaignId));
    setActiveTab(openPayload.activeTab === 'mailings' ? 'mailings' : 'settings');
    setSingleCampaignMode(true);
    navigate(location.pathname + (location.search || ''), { replace: true, state: null });
  }, [location.state, location.pathname, location.search, navigate]);

  // Загружаем кампании при изменении выбранной вакансии
  useEffect(() => {
    if (selectedVacancyId && (activeTab === 'mailings' || embedded)) {
      loadCampaigns();
    }
  }, [selectedVacancyId, activeTab, embedded]);

  // Загружаем данные выбранной кампании
  useEffect(() => {
    if (selectedCampaign && selectedVacancyId) {
      loadSelectedCampaignData();
    } else {
      setSelectedCampaignData(null);
    }
  }, [selectedCampaign, selectedVacancyId]);

  const loadSelectedCampaignData = async () => {
    if (!selectedCampaign || !selectedVacancyId) return;

    try {
      const campaign = await getCampaignById(selectedCampaign);
      setSelectedCampaignData(campaign);
    } catch (error) {
      console.error('Error loading campaign data:', error);
      setSelectedCampaignData(null);
    }
  };

  const loadCampaigns = async () => {
    if (!selectedVacancyId) {
      setCampaigns([]);
      return;
    }

    setLoadingCampaigns(true);
    try {
      // positionId фильтрует кампании по вакансии; X-Tenant-Id ставит прокси из JWT.
      const response = await getCampaigns({
        size: 1000,
        positionId: Number(selectedVacancyId),
      });
      const campaignsList = response.content || [];
      // Сортируем кампании в обратном порядке (новые сначала)
      const sortedCampaigns = [...campaignsList].sort((a, b) => {
        const versionDiff = (Number((b as any).version) || 0) - (Number((a as any).version) || 0);
        if (versionDiff !== 0) return versionDiff;
        // createdAt приходит как массив [год, месяц, день, час, минута, секунда, наносекунды]
        const timeA = a.createdAt && Array.isArray(a.createdAt)
          ? new Date(a.createdAt[0], a.createdAt[1] - 1, a.createdAt[2], a.createdAt[3], a.createdAt[4], a.createdAt[5]).getTime()
          : 0;
        const timeB = b.createdAt && Array.isArray(b.createdAt)
          ? new Date(b.createdAt[0], b.createdAt[1] - 1, b.createdAt[2], b.createdAt[3], b.createdAt[4], b.createdAt[5]).getTime()
          : 0;
        return timeB - timeA;
      });
      const latestCampaigns = sortedCampaigns.slice(0, 1);
      setCampaigns(latestCampaigns);
      if (latestCampaigns[0] && !selectedCampaign && (activeTab === 'mailings' || embedded)) {
        setSelectedCampaign(String(latestCampaigns[0].id));
        setActiveTab('settings');
      }

      const statsPromises = latestCampaigns.map(async (campaign) => {
        try {
          const stats: any = await getCampaignStats(campaign.id);
          const totalCandidates = stats?.totalCandidates ?? stats?.candidates ?? 0;
          const sourceTotals = stats?.sourceTotals ?? {};
          return { id: String(campaign.id), count: totalCandidates, sourceTotals };
        } catch {
          return { id: String(campaign.id), count: 0, sourceTotals: {} };
        }
      });

      const statsResults = await Promise.all(statsPromises);
      const countsMap: Record<string, number> = {};
      const sourceMap: Record<string, Record<string, number>> = {};
      statsResults.forEach(({ id, count, sourceTotals }) => {
        countsMap[id] = count;
        if (sourceTotals && Object.keys(sourceTotals).length > 0) {
          sourceMap[id] = sourceTotals;
        }
      });
      setCampaignCandidateCounts(countsMap);
      setCampaignSourceTotals(sourceMap);
    } catch (error) {
      console.error('Error loading campaigns:', error);
      toast.error('Ошибка загрузки рассылок');
      setCampaigns([]);
    } finally {
      setLoadingCampaigns(false);
    }
  };

  // Функция для получения статуса кампании
  const getCampaignStatus = (status: string) => {
    const statusMap: Record<string, { color: string; text: string }> = {
      'DRAFT': { color: '#9CA3AF', text: 'Черновик' },
      'ACTIVE': { color: '#10B981', text: 'Активна' },
      'PAUSED': { color: '#F97316', text: 'На паузе' },
      'STOPPED': { color: '#EF4444', text: 'Остановлена' },
      'CANCELLED': { color: '#EF4444', text: 'Отменена' },
      'ARCHIVED': { color: '#6B7280', text: 'В архиве' }
    };
    return statusMap[status] || { color: '#9CA3AF', text: status };
  };

  // Обработчики действий с кампанией
  const handleStartCampaign = async (campaignId: string | number) => {
    if (!selectedVacancyId) return;

    try {
      toast.loading('Запуск рассылки...', { id: 'start-campaign' });
      const companyId = useAuthStore.getState().user?.companyId;
      await startCampaign(campaignId, buildStartCampaignRequestConfig(companyId));
      toast.dismiss('start-campaign');
      toast.success('Рассылка запущена');
      loadCampaigns(); // Reload campaigns to update status
      if (selectedCampaign) {
        loadSelectedCampaignData(); // Reload selected campaign data
      }
    } catch (error: any) {
      console.error('Error starting campaign:', error);
      toast.dismiss('start-campaign');
      const backendMessage = error?.response?.data?.message;
      toast.error(backendMessage || 'Ошибка при запуске рассылки');
    }
  };

  const openCampaignSettings = (campaignId: string | number) => {
    setSelectedCampaign(String(campaignId));
    setActiveTab('settings');
  };

  const handleStartCampaignFromList = async (campaign: Campaign) => {
    if (!hasConfiguredStartChannel(campaign)) {
      openCampaignSettings(campaign.id);
      toast.error('Выберите канал рассылки в настройках перед запуском');
      return;
    }

    await handleStartCampaign(campaign.id);
  };

  useEffect(() => {
    if (!externalStartRef) return;
    if (!canStartSelectedCampaign || !selectedCampaign) {
      externalStartRef.current = null;
      onStartAvailableChange?.(false);
      return;
    }

    const startSelectedCampaign = async () => {
      if (validateStartHandlerRef.current && !validateStartHandlerRef.current()) {
        return;
      }
      if (saveHandlerRef.current) {
        const saved = await saveHandlerRef.current();
        if (!saved) return;
      }
      await handleStartCampaign(selectedCampaign);
    };

    externalStartRef.current = startSelectedCampaign;
    onStartAvailableChange?.(true);

    return () => {
      if (externalStartRef.current === startSelectedCampaign) {
        externalStartRef.current = null;
        onStartAvailableChange?.(false);
      }
    };
  }, [canStartSelectedCampaign, externalStartRef, onStartAvailableChange, saveHandlerRef, selectedCampaign]);

  const handleStopCampaign = async (campaignId: string | number) => {
    if (!selectedVacancyId) return;

    try {
      toast.loading('Остановка рассылки...', { id: 'stop-campaign' });
      await stopCampaign(campaignId);
      toast.dismiss('stop-campaign');
      toast.success('Рассылка остановлена');
      loadCampaigns(); // Reload campaigns to update status
      if (selectedCampaign) {
        loadSelectedCampaignData(); // Reload selected campaign data
      }
    } catch (error: any) {
      console.error('Error stopping campaign:', error);
      toast.dismiss('stop-campaign');
      const backendMessage = error?.response?.data?.message;
      toast.error(backendMessage || 'Ошибка при остановке рассылки');
    }
  };

  useEffect(() => {
    if (!externalStopRef) return;
    if (!canStopSelectedCampaign || !selectedCampaign) {
      externalStopRef.current = null;
      onStopAvailableChange?.(false);
      return;
    }

    const stopSelectedCampaign = async () => {
      await handleStopCampaign(selectedCampaign);
    };

    externalStopRef.current = stopSelectedCampaign;
    onStopAvailableChange?.(true);

    return () => {
      if (externalStopRef.current === stopSelectedCampaign) {
        externalStopRef.current = null;
        onStopAvailableChange?.(false);
      }
    };
  }, [canStopSelectedCampaign, externalStopRef, onStopAvailableChange, selectedCampaign]);

  const handleArchiveCampaign = async (campaignId: string | number) => {
    if (!selectedVacancyId) return;

    try {
      toast.loading('Архивирование рассылки...', { id: 'archive-campaign' });
      await archiveCampaign(campaignId);
      toast.dismiss('archive-campaign');
      toast.success('Рассылка архивирована');
      loadCampaigns(); // Reload campaigns to update status
      if (selectedCampaign) {
        loadSelectedCampaignData(); // Reload selected campaign data
      }
    } catch (error: any) {
      console.error('Error archiving campaign:', error);
      toast.dismiss('archive-campaign');
      const backendMessage = error?.response?.data?.message;
      toast.error(backendMessage || 'Ошибка при архивировании рассылки');
    }
  };

  // Получить доступные действия для кампании в зависимости от статуса
  const getAvailableActions = (status: string) => {
    const actions = [];

    if (status === 'DRAFT' || status === 'STOPPED' || status === 'PAUSED' || status === 'CANCELLED') {
      actions.push({ label: 'Запустить', action: 'start', color: 'text-green-600' });
    }

    if (status === 'ACTIVE') {
      actions.push({ label: 'Остановить', action: 'stop', color: 'text-red-600' });
    }

    if (status !== 'ARCHIVED') {
      actions.push({ label: 'Архивировать', action: 'archive', color: 'text-gray-600' });
    }

    return actions;
  };

  // Видимый список кампаний: фильтр по поисковому запросу + сортировка (только для табличного вида)
  const visibleCampaigns = (() => {
    let list = campaigns;
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(c => (c.name || '').toLowerCase().includes(q));
    }
    if (statusFilter) {
      list = list.filter(c => c.status === statusFilter);
    }
    if (channelFilter) {
      const filterChannel = normalizeCampaignChannel(channelFilter);
      list = list.filter(c => {
        if (!filterChannel) return true;
        return isCampaignChannelReady(c, filterChannel);
      });
    }
    if (sortField) {
      const sign = sortDir === 'asc' ? 1 : -1;
      const getKey = (c: Campaign): number | string => {
        if (sortField === 'name') return (c.name || '').toLowerCase();
        if (sortField === 'status') return statusRank[c.status as string] ?? 99;
        if (sortField === 'candidates') return campaignCandidateCounts[String(c.id)] ?? -1;
        if (sortField === 'channels') {
          return CAMPAIGN_CHANNEL_CODES.filter(channel => isCampaignChannelReady(c, channel)).length;
        }
        return 0;
      };
      list = [...list].sort((a, b) => {
        const ka = getKey(a); const kb = getKey(b);
        if (ka < kb) return -1 * sign;
        if (ka > kb) return 1 * sign;
        return 0;
      });
    }
    return list;
  })();

  return (
    <div className="h-full overflow-y-auto bg-[#F5F6F1]">
      {/* Action-button row — shown when a campaign is selected on the settings tab */}
      {!embedded && !loadingCampaigns && (campaigns.length > 0 || selectedCampaignData) && (
      <div className="bg-[#F5F6F1] px-8 py-6 flex items-center justify-between">
        {/* Left: back-to-list button, only visible on settings tab */}
        <AnimatePresence mode="wait" initial={false}>
        {activeTab === 'settings' && !embedded && !singleCampaignMode ? (
          <motion.button
            key="campaign-back"
            type="button"
            onClick={() => {
              setActiveTab('mailings');
              setSelectedCampaign(null);
            }}
            className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-[#E5E7EB] text-[#525866] transition-colors"
            title="К списку рассылок"
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1], delay: 0.05 }}
          >
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
              <path d="M12 4L6 10L12 16" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </motion.button>
        ) : (
          <span key="campaign-back-placeholder" />
        )}
        </AnimatePresence>
        {/* Right: action buttons - показываем только на табе настроек и если выбрана рассылка */}
        <AnimatePresence mode="wait" initial={false}>
        {activeTab === 'settings' && selectedCampaign && selectedCampaignData && String(selectedCampaignData.id) === selectedCampaign && (
          <motion.div
            key="campaign-actions"
            className="flex gap-3"
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 12 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1], delay: 0.05 }}
          >
            {/* Start button - не показываем для архивированных */}
            {selectedCampaignData.status !== 'ARCHIVED' &&
              (selectedCampaignData.status === 'DRAFT' ||
              selectedCampaignData.status === 'STOPPED' ||
              selectedCampaignData.status === 'PAUSED' ||
              selectedCampaignData.status === 'CANCELLED') && (
              <button
                onClick={async () => {
                  // Сначала сохраняем, потом запускаем
                  if (saveHandlerRef.current) {
                    try {
                      if (validateStartHandlerRef.current && !validateStartHandlerRef.current()) {
                        return;
                      }
                      const saved = await saveHandlerRef.current();
                      if (!saved) return;
                      await handleStartCampaign(selectedCampaign);
                    } catch (error) {
                      console.error('Error saving before start:', error);
                      toast.error('Ошибка сохранения перед запуском');
                    }
                  } else {
                    await handleStartCampaign(selectedCampaign);
                  }
                }}
                className="px-6 py-2 text-sm font-semibold rounded-lg text-white bg-green-600 hover:bg-green-700 transition-colors"
              >
                Запустить
              </button>
            )}

            {/* Stop button - не показываем для архивированных */}
            {selectedCampaignData.status !== 'ARCHIVED' &&
              selectedCampaignData.status === 'ACTIVE' && (
              <button
                onClick={() => handleStopCampaign(selectedCampaign)}
                className="px-6 py-2 text-sm font-semibold rounded-lg text-white bg-red-600 hover:bg-red-700 transition-colors"
              >
                Остановить
              </button>
            )}

            {/* Archive button - скрыто на данный момент
            {selectedCampaignData.status !== 'ARCHIVED' && (
              <button
                onClick={() => handleArchiveCampaign(selectedCampaign)}
                className="px-6 py-2 text-sm font-semibold rounded-lg text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors"
              >
                Архивировать
              </button>
            )} */}

            {!embedded && singleCampaignMode && (
              <button
                onClick={() => navigate(-1)}
                className="px-6 py-2 text-sm font-semibold rounded-lg text-gray-900 bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                Отмена
              </button>
            )}

            {!embedded && (
              <button
                onClick={() => {
                  if (saveHandlerRef.current) {
                    saveHandlerRef.current();
                  }
                }}
                className="px-6 py-2 text-sm font-semibold rounded-lg text-white transition-colors"
                style={{ backgroundColor: colors.primary }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.primaryHover; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.primary; }}
              >
                Сохранить
              </button>
            )}
          </motion.div>
        )}
        </AnimatePresence>
      </div>
      )}

      {/* Контент в зависимости от выбранного таба */}
      <AnimatePresence mode="wait" initial={false}>
      {activeTab === 'mailings' ? (
        <motion.div
          key="mailings-list"
          className={`${hPadding} pb-6`}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
        >
          {loadingCampaigns ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderBottomColor: colors.primary }}></div>
                <p className="mt-2 text-gray-600">Загрузка рассылок...</p>
              </div>
            </div>
          ) : !selectedVacancyId ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-gray-500">Выберите вакансию для просмотра рассылок</p>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-gray-500">Нет рассылок для этой вакансии</p>
            </div>
          ) : (
            <>
              {/* Toolbar: поиск + фильтры слева (прижаты друг к другу), переключатель вида справа. */}
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2 flex-1 min-w-0 pl-1">
                <div className="relative w-full max-w-xs">
                  <svg
                    className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none transition-colors duration-200"
                    viewBox="0 0 24 24" fill="none" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
                    stroke={(searchHover || searchFocus || !!searchQuery) ? colors.primary : '#9CA3AF'}
                  >
                    <circle cx="11" cy="11" r="7" />
                    <path d="m21 21-4.3-4.3" />
                  </svg>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Поиск по названию рассылки"
                    className="w-full h-9 pl-9 pr-3 text-sm bg-white rounded-md focus:outline-none transition-all duration-200"
                    style={{
                      color: (searchHover || searchFocus || !!searchQuery) ? colors.primary : '#374151',
                      boxShadow: (searchHover || searchFocus || !!searchQuery)
                        ? `0 0 0 1px ${colors.primary}, 0px 0px 15px rgba(0,0,0,0.09)`
                        : '0px 0px 15px rgba(0,0,0,0.09)',
                      border: 'none',
                    }}
                    onMouseEnter={() => setSearchHover(true)}
                    onMouseLeave={() => setSearchHover(false)}
                    onFocus={() => setSearchFocus(true)}
                    onBlur={() => setSearchFocus(false)}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded"
                      title="Очистить"
                    >
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 6 6 18M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Фильтр по статусу — дропдаун с радио-опциями в стиле Uiverse */}
                <div className="relative" ref={statusDropdownRef}>
                  {(() => {
                    const selected = STATUS_OPTIONS.find(o => o.value === statusFilter) || STATUS_OPTIONS[0];
                    const isActive = !!statusFilter;
                    const showTint = isActive || statusHover || statusOpen;
                    return (
                      <button
                        type="button"
                        onClick={() => setStatusOpen(v => !v)}
                        onMouseEnter={() => setStatusHover(true)}
                        onMouseLeave={() => setStatusHover(false)}
                        className="h-9 pl-3 pr-8 text-sm rounded-md focus:outline-none transition-all duration-200 cursor-pointer inline-flex items-center justify-start whitespace-nowrap relative min-w-[160px]"
                        style={{
                          color: showTint ? colors.primary : '#374151',
                          backgroundColor: '#ffffff',
                          boxShadow: showTint
                            ? `0 0 0 1px ${colors.primary}, 0px 0px 15px rgba(0,0,0,0.09)`
                            : '0px 0px 15px rgba(0,0,0,0.09)',
                        }}
                      >
                        {selected.label}
                        <svg className={`w-4 h-4 absolute right-2 transition-transform duration-200 ${statusOpen ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke={showTint ? colors.primary : '#9CA3AF'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                          <path d="m6 9 6 6 6-6" />
                        </svg>
                      </button>
                    );
                  })()}
                  {statusOpen && (
                    <div className="absolute top-full left-0 mt-2 w-[240px] px-4 py-4 bg-white flex flex-col gap-2 rounded-md shadow-[0px_0px_15px_rgba(0,0,0,0.09)] z-20 ai-fade-in">
                      <div className="text-[13px] font-semibold mb-1 select-none text-gray-700">Статус</div>
                      {STATUS_OPTIONS.map(opt => {
                        const checked = statusFilter === opt.value;
                        return (
                          <label
                            key={opt.value || 'all'}
                            htmlFor={`status-${opt.value || 'all'}`}
                            className="relative flex items-center px-3 py-2 rounded-md cursor-pointer transition-colors text-sm text-gray-700 hover:bg-gray-50 select-none"
                            style={checked ? { backgroundColor: hexToRgba(colors.primary, 0.08), color: colors.primary } : {}}
                          >
                            {opt.label}
                            <input
                              type="radio"
                              id={`status-${opt.value || 'all'}`}
                              name="statusFilter"
                              checked={checked}
                              onChange={() => { setStatusFilter(opt.value); setStatusOpen(false); }}
                              className="w-4 h-4 absolute right-3"
                              style={{ accentColor: colors.primary }}
                            />
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Фильтр по активному каналу связи — такой же стиль */}
                <div className="relative" ref={channelDropdownRef}>
                  {(() => {
                    const selected = CHANNEL_OPTIONS.find(o => o.value === channelFilter) || CHANNEL_OPTIONS[0];
                    const isActive = !!channelFilter;
                    const showTint = isActive || channelHover || channelOpen;
                    return (
                      <button
                        type="button"
                        onClick={() => setChannelOpen(v => !v)}
                        onMouseEnter={() => setChannelHover(true)}
                        onMouseLeave={() => setChannelHover(false)}
                        className="h-9 pl-3 pr-8 text-sm rounded-md focus:outline-none transition-all duration-200 cursor-pointer inline-flex items-center justify-start whitespace-nowrap relative min-w-[160px]"
                        style={{
                          color: showTint ? colors.primary : '#374151',
                          backgroundColor: '#ffffff',
                          boxShadow: showTint
                            ? `0 0 0 1px ${colors.primary}, 0px 0px 15px rgba(0,0,0,0.09)`
                            : '0px 0px 15px rgba(0,0,0,0.09)',
                        }}
                      >
                        {selected.label}
                        <svg className={`w-4 h-4 absolute right-2 transition-transform duration-200 ${channelOpen ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke={showTint ? colors.primary : '#9CA3AF'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                          <path d="m6 9 6 6 6-6" />
                        </svg>
                      </button>
                    );
                  })()}
                  {channelOpen && (
                    <div className="absolute top-full left-0 mt-2 w-[200px] px-4 py-4 bg-white flex flex-col gap-2 rounded-md shadow-[0px_0px_15px_rgba(0,0,0,0.09)] z-20 ai-fade-in">
                      <div className="text-[13px] font-semibold mb-1 select-none text-gray-700">Канал связи</div>
                      {CHANNEL_OPTIONS.map(opt => {
                        const checked = channelFilter === opt.value;
                        return (
                          <label
                            key={opt.value || 'all'}
                            htmlFor={`channel-${opt.value || 'all'}`}
                            className="relative flex items-center px-3 py-2 rounded-md cursor-pointer transition-colors text-sm text-gray-700 hover:bg-gray-50 select-none"
                            style={checked ? { backgroundColor: hexToRgba(colors.primary, 0.08), color: colors.primary } : {}}
                          >
                            {opt.label}
                            <input
                              type="radio"
                              id={`channel-${opt.value || 'all'}`}
                              name="channelFilter"
                              checked={checked}
                              onChange={() => { setChannelFilter(opt.value); setChannelOpen(false); }}
                              className="w-4 h-4 absolute right-3"
                              style={{ accentColor: colors.primary }}
                            />
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Сброс фильтров — показываем только когда хоть один фильтр активен */}
                {(searchQuery || statusFilter || channelFilter) && (
                  <button
                    type="button"
                    onClick={() => { setSearchQuery(''); setStatusFilter(''); setChannelFilter(''); }}
                    className="h-9 px-3 text-sm rounded-md transition-all duration-200 cursor-pointer inline-flex items-center gap-1.5 ai-fade-in"
                    style={{ color: colors.primary, backgroundColor: 'transparent' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = hexToRgba(colors.primary, 0.08); }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; }}
                    title="Сбросить все фильтры"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                    Сбросить
                  </button>
                )}
                </div>
                <div className="inline-flex rounded-lg bg-white border border-gray-200 p-1 gap-0.5 shadow-sm">
                  {(() => {
                    const gridIconColor = campaignsView === 'grid' || gridHover ? colors.primary : '#9CA3AF';
                    const gridBg = campaignsView === 'grid'
                      ? hexToRgba(colors.primary, 0.22)
                      : gridHover ? hexToRgba(colors.primary, 0.08) : 'transparent';
                    return (
                      <button
                        type="button"
                        onClick={() => setCampaignsView('grid')}
                        className="w-8 h-8 inline-flex items-center justify-center rounded-md transition-all"
                        style={{ backgroundColor: gridBg }}
                        onMouseEnter={() => setGridHover(true)}
                        onMouseLeave={() => setGridHover(false)}
                        title="Сетка"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill={gridIconColor}>
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M9 3a2 2 0 0 1 2 2v4a2 2 0 0 1 -2 2h-4a2 2 0 0 1 -2 -2v-4a2 2 0 0 1 2 -2z" />
                          <path d="M19 3a2 2 0 0 1 2 2v4a2 2 0 0 1 -2 2h-4a2 2 0 0 1 -2 -2v-4a2 2 0 0 1 2 -2z" />
                          <path d="M9 13a2 2 0 0 1 2 2v4a2 2 0 0 1 -2 2h-4a2 2 0 0 1 -2 -2v-4a2 2 0 0 1 2 -2z" />
                          <path d="M19 13a2 2 0 0 1 2 2v4a2 2 0 0 1 -2 2h-4a2 2 0 0 1 -2 -2v-4a2 2 0 0 1 2 -2z" />
                        </svg>
                      </button>
                    );
                  })()}
                  {(() => {
                    const tableIconColor = campaignsView === 'table' || tableHover ? colors.primary : '#9CA3AF';
                    const tableBg = campaignsView === 'table'
                      ? hexToRgba(colors.primary, 0.22)
                      : tableHover ? hexToRgba(colors.primary, 0.08) : 'transparent';
                    return (
                      <button
                        type="button"
                        onClick={() => setCampaignsView('table')}
                        className="w-8 h-8 inline-flex items-center justify-center rounded-md transition-all"
                        style={{ backgroundColor: tableBg }}
                        onMouseEnter={() => setTableHover(true)}
                        onMouseLeave={() => setTableHover(false)}
                        title="Таблица"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke={tableIconColor} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M3 5a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v14a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-14" />
                          <path d="M3 10h18" />
                          <path d="M10 3v18" />
                        </svg>
                      </button>
                    );
                  })()}
                </div>
              </div>

            <AnimatePresence mode="wait" initial={false}>
            {visibleCampaigns.length === 0 && campaignsView === 'grid' ? (
              <motion.div
                key="campaigns-empty-filter"
                className="flex items-center justify-center min-h-[400px]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                <p className="text-gray-500 text-sm">Ничего не найдено</p>
              </motion.div>
            ) : campaignsView === 'table' ? (
              <motion.div
                key="campaigns-table"
                className="bg-white rounded-xl overflow-hidden"
                style={{ boxShadow: '0px 0px 15px rgba(0,0,0,0.09)' }}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {([
                          { field: 'name' as const, label: 'Название' },
                          { field: 'status' as const, label: 'Статус' },
                          { field: 'channels' as const, label: 'Каналы связи' },
                          { field: 'candidates' as const, label: 'Кандидаты' },
                        ]).map(col => {
                          const isActive = sortField === col.field;
                          return (
                            <th
                              key={col.field}
                              scope="col"
                              onClick={() => toggleSort(col.field)}
                              className="px-4 py-2.5 text-left text-[12px] font-bold select-none cursor-pointer transition-colors"
                              style={{
                                color: isActive ? colors.primary : '#6B7280',
                                backgroundColor: isActive ? hexToRgba(colors.primary, 0.08) : 'transparent',
                              }}
                              onMouseEnter={(e) => {
                                if (!isActive) {
                                  (e.currentTarget as HTMLTableCellElement).style.color = colors.primary;
                                  (e.currentTarget as HTMLTableCellElement).style.backgroundColor = hexToRgba(colors.primary, 0.08);
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isActive) {
                                  (e.currentTarget as HTMLTableCellElement).style.color = '#6B7280';
                                  (e.currentTarget as HTMLTableCellElement).style.backgroundColor = 'transparent';
                                }
                              }}
                            >
                              <span className="inline-flex items-center gap-1">
                                {col.label}
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" style={{ opacity: isActive ? 1 : 0.3 }}>
                                  {isActive && sortDir === 'desc'
                                    ? <path d="M6 9l6 6 6-6" />
                                    : <path d="M6 15l6-6 6 6" />}
                                </svg>
                              </span>
                            </th>
                          );
                        })}
                        <th scope="col" className="px-4 py-2.5 w-10" />
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {visibleCampaigns.length === 0 && (
                        <tr className="ai-fade-in">
                          <td colSpan={5} className="px-4 py-16 text-center text-gray-500 text-sm">
                            Ничего не найдено
                          </td>
                        </tr>
                      )}
                      {visibleCampaigns.map((campaign) => {
                        const statusInfo = getCampaignStatus(campaign.status);
                        const availableActions = getAvailableActions(campaign.status);
                        const count = campaignCandidateCounts[String(campaign.id)];
                        return (
                          <tr
                            key={campaign.id}
                            className={`${campaign.status !== 'ARCHIVED' ? 'cursor-pointer' : 'cursor-default opacity-80'} transition-colors duration-200`}
                            onMouseEnter={(e) => {
                              if (campaign.status !== 'ARCHIVED') {
                                (e.currentTarget as HTMLTableRowElement).style.backgroundColor = hexToRgba(colors.primary, 0.06);
                              }
                            }}
                            onMouseLeave={(e) => {
                              (e.currentTarget as HTMLTableRowElement).style.backgroundColor = '';
                            }}
                            onClick={() => {
                              if (campaign.status !== 'ARCHIVED') {
                                setSelectedCampaign(String(campaign.id));
                                setActiveTab('settings');
                              }
                            }}
                          >
                            <td className="px-4 py-2.5 text-[13px] text-[#1F1F1F] font-medium">{campaign.name}</td>
                            <td className="px-4 py-2.5 text-[12px] text-[#1F1F1F]">
                              <span className="inline-flex items-center gap-1.5">
                                <span className="relative inline-flex w-2 h-2">
                                {campaign.status === 'ACTIVE' && (
                                  <span className="absolute inset-0 rounded-full animate-ping" style={{ backgroundColor: statusInfo.color, opacity: 0.6 }} />
                                )}
                                <span className="relative inline-block w-2 h-2 rounded-full" style={{ backgroundColor: statusInfo.color }} />
                              </span>
                                {statusInfo.text}
                              </span>
                            </td>
                            <td className="px-4 py-2.5">
                              {(() => {
                                // Иконки — брендовые SVG, лежат в src/assets/icons/channels/.
                                const channels: Array<{ key: string; label: string; active: boolean; icon: string }> = [
                                  { key: 'HH', label: 'HH', active: isCampaignChannelReady(campaign, 'HH'), icon: hhIcon },
                                  { key: 'TG', label: 'Telegram', active: isCampaignChannelReady(campaign, 'TG'), icon: telegramIcon },
                                  { key: 'WA', label: 'WhatsApp', active: isCampaignChannelReady(campaign, 'WA'), icon: whatsappIcon },
                                  { key: 'MAX', label: 'Max', active: isCampaignChannelReady(campaign, 'MAX'), icon: maxIcon },
                                  { key: 'AVITO', label: 'Авито', active: isCampaignChannelReady(campaign, 'AVITO'), icon: avitoIcon },
                                ];
                                return (
                                  <div className="flex items-center gap-2">
                                    {channels.map(ch => (
                                      <img
                                        key={ch.key}
                                        src={ch.icon}
                                        alt={ch.label}
                                        title={ch.active ? `${ch.label} подключен` : `${ch.label} не настроен`}
                                        className={`w-6 h-6 transition-opacity ${ch.active ? 'opacity-100' : 'opacity-30 grayscale'}`}
                                      />
                                    ))}
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="px-4 py-2.5 text-[12px] text-[#1F1F1F]">
                              {count != null ? `${count} ${getCandidateWord(count)}` : '—'}
                            </td>
                            <td className="px-4 py-2.5 w-10">
                              {availableActions.length > 0 && (
                                <Menu as="div" className="relative" onClick={(e) => e.stopPropagation()}>
                                  <Menu.Button
                                  className="w-8 h-8 inline-flex items-center justify-center rounded-md transition-all duration-200 text-[#9B9B9B]"
                                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = hexToRgba(colors.primary, 0.08); (e.currentTarget as HTMLButtonElement).style.color = colors.primary; }}
                                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = '#9B9B9B'; }}
                                >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                      <circle cx="8" cy="4" r="1" fill="currentColor"/>
                                      <circle cx="8" cy="8" r="1" fill="currentColor"/>
                                      <circle cx="8" cy="12" r="1" fill="currentColor"/>
                                    </svg>
                                  </Menu.Button>
                                  <Transition as={Fragment} enter="transition ease-out duration-100" enterFrom="transform opacity-0 scale-95" enterTo="transform opacity-100 scale-100" leave="transition ease-in duration-75" leaveFrom="transform opacity-100 scale-100" leaveTo="transform opacity-0 scale-95">
                                    <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                                      <div className="py-1">
                                        {availableActions.map((actionItem) => (
                                          <Menu.Item key={actionItem.action}>
                                            {({ active }) => (
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  if (actionItem.action === 'start') void handleStartCampaignFromList(campaign);
                                                  else if (actionItem.action === 'stop') handleStopCampaign(campaign.id);
                                                  else if (actionItem.action === 'archive') handleArchiveCampaign(campaign.id);
                                                }}
                                                className={`${active ? 'bg-gray-100' : ''} ${actionItem.color} block w-full px-4 py-2 text-left text-sm`}
                                              >
                                                {actionItem.label}
                                              </button>
                                            )}
                                          </Menu.Item>
                                        ))}
                                      </div>
                                    </Menu.Items>
                                  </Transition>
                                </Menu>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            ) : (
            <motion.div
              key="campaigns-grid"
              className="grid grid-cols-4 gap-4 pl-1 pr-1 py-1"
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0, y: -8, transition: { duration: 0.2, ease: [0.16, 1, 0.3, 1] } }}
              variants={{
                hidden: { opacity: 0, y: 8 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.2, ease: [0.16, 1, 0.3, 1], staggerChildren: 0.05, delayChildren: 0.05 } },
              }}
            >
              {visibleCampaigns.map((campaign) => {
                const statusInfo = getCampaignStatus(campaign.status);
                const availableActions = getAvailableActions(campaign.status);
                const isHovered = hoveredCampaignId === String(campaign.id);
                const canInteract = campaign.status !== 'ARCHIVED';
                const count = campaignCandidateCounts[String(campaign.id)];
                const channels: Array<{ key: string; label: string; active: boolean; icon: string }> = [
                  { key: 'HH', label: 'HH', active: isCampaignChannelReady(campaign, 'HH'), icon: hhIcon },
                  { key: 'TG', label: 'Telegram', active: isCampaignChannelReady(campaign, 'TG'), icon: telegramIcon },
                  { key: 'WA', label: 'WhatsApp', active: isCampaignChannelReady(campaign, 'WA'), icon: whatsappIcon },
                  { key: 'MAX', label: 'Max', active: isCampaignChannelReady(campaign, 'MAX'), icon: maxIcon },
                  { key: 'AVITO', label: 'Авито', active: isCampaignChannelReady(campaign, 'AVITO'), icon: avitoIcon },
                ];

                return (
                  <motion.div
                    key={campaign.id}
                    variants={{
                      hidden: { opacity: 0, y: 12 },
                      visible: { opacity: 1, y: 0, transition: { duration: 0.22, ease: [0.16, 1, 0.3, 1] } },
                    }}
                    className={`relative rounded-xl p-4 transition-all duration-200 ${canInteract ? 'cursor-pointer' : 'cursor-default opacity-80'}`}
                    style={{
                      backgroundColor: '#ffffff',
                      boxShadow: isHovered && canInteract
                        ? `0 0 0 1px ${colors.primary}, 0px 0px 15px rgba(0,0,0,0.09)`
                        : '0px 0px 15px rgba(0,0,0,0.09)',
                    }}
                    onMouseEnter={() => canInteract && setHoveredCampaignId(String(campaign.id))}
                    onMouseLeave={() => setHoveredCampaignId(null)}
                    onClick={() => {
                      if (canInteract) {
                        setSelectedCampaign(String(campaign.id));
                        setActiveTab('settings');
                      }
                    }}
                  >
                    {/* Меню действий — абсолютно в верхнем правом углу */}
                    {availableActions.length > 0 && (
                      <Menu as="div" className="absolute top-3 right-3 z-10" onClick={(e) => e.stopPropagation()}>
                        <Menu.Button
                          className="w-8 h-8 inline-flex items-center justify-center rounded-md transition-all duration-200 text-[#9B9B9B]"
                          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = hexToRgba(colors.primary, 0.08); (e.currentTarget as HTMLButtonElement).style.color = colors.primary; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = '#9B9B9B'; }}
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <circle cx="8" cy="4" r="1" fill="currentColor"/>
                            <circle cx="8" cy="8" r="1" fill="currentColor"/>
                            <circle cx="8" cy="12" r="1" fill="currentColor"/>
                          </svg>
                        </Menu.Button>
                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-100"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                            <div className="py-1">
                              {availableActions.map((actionItem) => (
                                <Menu.Item key={actionItem.action}>
                                  {({ active }) => (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (actionItem.action === 'start') void handleStartCampaignFromList(campaign);
                                        else if (actionItem.action === 'stop') handleStopCampaign(campaign.id);
                                        else if (actionItem.action === 'archive') handleArchiveCampaign(campaign.id);
                                      }}
                                      className={`${active ? 'bg-gray-100' : ''} ${actionItem.color} block w-full px-4 py-2 text-left text-sm`}
                                    >
                                      {actionItem.label}
                                    </button>
                                  )}
                                </Menu.Item>
                              ))}
                            </div>
                          </Menu.Items>
                        </Transition>
                      </Menu>
                    )}

                    {/* Title */}
                    <h3 className="text-[1.1rem] font-bold pr-10 leading-tight line-clamp-2 text-[#1F1F1F]">
                      {campaign.name}
                    </h3>

                    {/* Status */}
                    <div className="flex items-center gap-2 text-[0.86rem] mt-[1.1em]" style={{ color: '#3c3852' }}>
                      <span className="relative inline-flex w-2 h-2">
                                {campaign.status === 'ACTIVE' && (
                                  <span className="absolute inset-0 rounded-full animate-ping" style={{ backgroundColor: statusInfo.color, opacity: 0.6 }} />
                                )}
                                <span className="relative inline-block w-2 h-2 rounded-full" style={{ backgroundColor: statusInfo.color }} />
                              </span>
                      {statusInfo.text}
                    </div>

                    {/* Channels */}
                    <div className="flex items-center gap-2 mt-[1.1em]">
                      {channels.map(ch => (
                        <img
                          key={ch.key}
                          src={ch.icon}
                          alt={ch.label}
                          title={ch.active ? `${ch.label} подключен` : `${ch.label} не настроен`}
                          className={`w-6 h-6 transition-opacity ${ch.active ? 'opacity-100' : 'opacity-30 grayscale'}`}
                        />
                      ))}
                    </div>

                    {/* Candidate count — мутный подзаголовок */}
                    <div className="text-[0.8rem] mt-[1.1em] pr-14" style={{ color: '#6e6b80' }}>
                      {count != null ? `${count} ${getCandidateWord(count)}` : 'Нет кандидатов'}
                    </div>

                    {/* Стрелка в правом нижнем углу — bg меняется на hover, svg сдвигается */}
                    {canInteract && (
                      <div
                        className="absolute bottom-0 right-0 p-[0.4rem] flex items-center justify-center transition-all duration-200"
                        style={{
                          backgroundColor: isHovered ? '#111' : colors.primary,
                          borderTopLeftRadius: '0.75rem',
                          borderBottomRightRadius: '0.75rem',
                        }}
                      >
                        <svg
                          className="w-5 h-5 transition-transform duration-200"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#ffffff"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ transform: isHovered ? 'translateX(3px)' : 'translateX(0)' }}
                        >
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </motion.div>
            )}
            </AnimatePresence>
            </>
          )}
        </motion.div>
      ) : (
        <motion.div
          key="mailings-settings"
          className="bg-[#f5f6f1] flex-1 overflow-hidden"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
        >
          <VacanciesTab
            selectedCampaign={selectedCampaign}
            setSelectedCampaign={setSelectedCampaign}
            onSave={saveHandlerRef}
            onValidateStart={validateStartHandlerRef}
            embedded={embedded}
          />
        </motion.div>
      )}
      </AnimatePresence>
    </div>
  );
};

export default MailingsPage;
